import { IMenuItem, IMenuNodeResult } from "@/types/types";
import { matchPath } from "react-router";

/**
 * 检查节点的key是否匹配给定的key, 按照react-router的path匹配规则
 * @param node - 菜单节点
 * @param key - 要匹配的key
 * @returns 如果匹配则返回true, 否则返回false
 */
const isMatchPath = ({ node, key }: { node: IMenuItem; key: string }) => {
  if (node.key && key) {
    const res = matchPath({ path: node.key, end: true }, key);
    return res !== null;
  }
  return false;
};

/**
 * 递归遍历操作menuTree的方法
 * @param node - 菜单节点
 * @param cb - 回调函数
 */
export const traverseMenuTree = (
  node: IMenuItem,
  cb?: (node: IMenuItem) => void,
): void => {
  // if (node.subMenus) {
  //   node.children = node.subMenus;
  //   delete node.subMenus;
  // }
  if (cb) cb(node); // 执行回调
  if (node.children?.length) {
    for (let i = 0; i < node.children.length; i++) {
      traverseMenuTree(node.children[i], cb);
    }
  }
};

/**
 * 过滤掉不可见的节点
 * @param nodes - 菜单节点
 * @returns 过滤后的菜单节点
 */
export const filterUnVisibleNodes = (node: IMenuItem): IMenuItem => {
  const filterNodes = (nodes: IMenuItem[]): IMenuItem[] => {
    return nodes.filter((node) => {
      if (node.children) {
        node.children = filterNodes(node.children);
      }
      return node.visible !== false;
    });
  };

  if (!node || node.visible === false) {
    return null;
  }

  return {
    ...node,
    children: filterNodes(node.children || []),
  };
};

/**
 * 递归遍历查找menuTree树节点的方法
 * @param node - 菜单节点
 * @param key - 要查找的key
 * @returns 找到的节点或null
 */
export const findMenuTreeNode = (
  node: IMenuItem,
  key: string,
): IMenuItem | null => {
  if (isMatchPath({ node, key })) {
    return node;
  }
  if (node.children?.length) {
    for (let i = 0; i < node.children.length; i++) {
      const result = findMenuTreeNode(node.children[i], key);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

/**
 * 增强版递归查找方法
 * @param node - 当前遍历的节点
 * @param targetKey - 要查找的目标key
 * @param currentPath - 当前路径（内部递归使用）
 * @returns 查找结果对象或null
 */
export const findMenuTreeNodeAndParentKeys = (
  node: IMenuItem,
  targetKey: string,
  currentPath: string[] = [],
): IMenuNodeResult | null => {
  // 找到目标节点时返回结果
  if (isMatchPath({ node, key: targetKey })) {
    return {
      node,
      parentKeys: [...currentPath], // 返回父级路径副本
    };
  }

  // 处理子节点
  if (node.children?.length) {
    for (const child of node.children) {
      const newPath = node.key ? [...currentPath, node.key] : currentPath;
      // 递归时传递当前路径 + 当前节点的key
      const result = findMenuTreeNodeAndParentKeys(child, targetKey, newPath);
      if (result) return result;
    }
  }

  return null;
};

/**
 * 递归查找, 并返回父节点路径
 * @param node - 当前遍历的节点
 * @param targetKey - 要查找的目标key
 * @param current - 当前路径（内部递归使用）
 * @returns 查找结果对象或null
 */
export const findMenuTreeNodeAndParents = (
  node: IMenuItem,
  targetKey: string,
  current: IMenuItem[] = [],
): IMenuNodeResult | null => {
  // 找到目标节点时返回结果
  if (isMatchPath({ node, key: targetKey })) {
    return {
      node,
      parents: [...current], // 返回父级路径副本
    };
  }

  // 处理子节点
  if (node.children?.length) {
    for (const child of node.children) {
      const newList = node.key ? [...current, node] : current;
      // 递归时传递当前路径 + 当前节点的key
      const result = findMenuTreeNodeAndParents(child, targetKey, newList);
      if (result) return result;
    }
  }

  return null;
};

/**
 * 通过方法匹配结果
 * @param node - 菜单节点
 * @param func - 匹配函数
 * @returns 匹配到的节点或null
 */
export const matchMenuTreeNode = (
  node: IMenuItem,
  func: (node: IMenuItem) => boolean,
): IMenuItem | null => {
  if (func(node)) {
    return node;
  }
  if (node.children?.length) {
    for (let i = 0; i < node.children.length; i++) {
      const result = matchMenuTreeNode(node.children[i], func);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

/**
 * 根据菜单项 type 判断是否为低码页面
 * @param type
 */
export const isLowcodePage = (type: IMenuItem["type"]): boolean => {
  return type === "lowcode";
};
